import { Download, FileText, Check, AlertCircle, Loader2 } from 'lucide-react';

interface OCRPdfDisplayProps {
  result: any;
  theme?: 'light' | 'dark';
  showToast?: (message: string) => void;
  addToResources?: (resource: any) => void;
  showHeader?: boolean;
}

export default function OCRPdfDisplay({
  result,
  theme = 'light',
  showToast,
  addToResources,
  showHeader = true
}: OCRPdfDisplayProps) {
  // Check if this result contains OCR data
  if (!result || typeof result !== 'object') return null;

  const isOCRResult = result.ocr_id !== undefined ||
                      result.download_url !== undefined ||
                      result.output_filename !== undefined ||
                      result.output_s3_key !== undefined ||
                      result.page_count !== undefined;

  if (!isOCRResult) return null;

  const downloadFile = async () => {
    if (!result.download_url) {
      showToast?.('Download URL not available');
      return;
    }

    try {
      // Open the presigned URL in a new tab to trigger download
      const link = document.createElement('a');
      link.href = result.download_url;
      link.download = result.output_filename || 'ocr_output.txt';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      showToast?.('Download started!');
    } catch (error) {
      console.error('Error downloading file:', error);
      showToast?.('Error downloading file');
    }
  };

  const copyDownloadLink = async () => {
    if (!result.download_url) {
      showToast?.('Download URL not available');
      return;
    }

    try {
      await navigator.clipboard.writeText(result.download_url);
      showToast?.('Download link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy:', error);
      showToast?.('Failed to copy link to clipboard');
    }
  };

  // Determine status
  const status = result.status || 'complete';
  const isComplete = status === 'complete' || status === 'completed';
  const isError = status.toString().toLowerCase().includes('error');
  const isProcessing = status === 'processing' || status === 'queued';

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="border-b pb-2 mb-4">
          <h3 className="text-lg font-semibold">PDF OCR Processing</h3>
        </div>
      )}

      <div className="space-y-4">
        {/* Status Header */}
        <div className={`p-4 rounded-lg border ${
          isComplete ? 'bg-green-50 border-green-200' :
          isError ? 'bg-red-50 border-red-200' :
          'bg-blue-50 border-blue-200'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isComplete && <Check className="w-5 h-5 text-green-600" />}
              {isError && <AlertCircle className="w-5 h-5 text-red-600" />}
              {isProcessing && <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />}

              <div>
                <span className={`font-semibold text-sm ${
                  isComplete ? 'text-green-900' :
                  isError ? 'text-red-900' :
                  'text-blue-900'
                }`}>
                  {isComplete && 'OCR Complete'}
                  {isError && 'OCR Failed'}
                  {isProcessing && 'Processing OCR...'}
                </span>
                {result.output_filename && (
                  <p className="text-xs text-gray-600 mt-1">
                    Output: {result.output_filename}
                  </p>
                )}
              </div>
            </div>

            {isComplete && result.download_url && (
              <button
                onClick={downloadFile}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                Download {result.output_format === 'json' ? 'JSON' : result.output_format === 'docx' ? 'Word' : 'Text'}
              </button>
            )}
          </div>
        </div>

        {/* OCR Details */}
        {isComplete && (
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
              <h4 className="font-semibold text-sm text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                OCR Details
              </h4>
            </div>
            <div className="p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {result.output_filename && (
                  <div>
                    <span className="font-medium text-gray-700">File Name:</span>
                    <p className="text-gray-600 mt-1">{result.output_filename}</p>
                  </div>
                )}
                {result.output_format && (
                  <div>
                    <span className="font-medium text-gray-700">Format:</span>
                    <p className="text-gray-600 mt-1 uppercase">{result.output_format}</p>
                  </div>
                )}
                {result.page_count !== undefined && (
                  <div>
                    <span className="font-medium text-gray-700">Pages Processed:</span>
                    <p className="text-gray-600 mt-1">{result.page_count}</p>
                  </div>
                )}
                {result.ocr_id && (
                  <div>
                    <span className="font-medium text-gray-700">OCR ID:</span>
                    <p className="text-gray-600 mt-1 font-mono text-xs">{result.ocr_id}</p>
                  </div>
                )}
                {result.ocr_timestamp && (
                  <div>
                    <span className="font-medium text-gray-700">Completed:</span>
                    <p className="text-gray-600 mt-1">
                      {new Date(result.ocr_timestamp).toLocaleString()}
                    </p>
                  </div>
                )}
                {result.source_type && (
                  <div>
                    <span className="font-medium text-gray-700">Source Type:</span>
                    <p className="text-gray-600 mt-1 capitalize">{result.source_type}</p>
                  </div>
                )}
              </div>

              {result.download_url && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-medium text-gray-700 text-sm">Download Link</span>
                      <p className="text-xs text-gray-500 mt-1">Valid for 7 days</p>
                    </div>
                    <button
                      onClick={copyDownloadLink}
                      className="text-sm text-blue-600 hover:text-blue-700 underline"
                    >
                      Copy Link
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Error Details */}
        {isError && result.error_message && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-sm text-red-900 mb-2">Error Details</h4>
            <p className="text-sm text-red-700 whitespace-pre-wrap">{result.error_message}</p>
          </div>
        )}

        {/* Processing Status */}
        {isProcessing && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <div>
                <h4 className="font-semibold text-sm text-blue-900">Processing PDF with OCR</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Your PDF document is being processed with optical character recognition. This may take a few moments...
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Information Note */}
        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
          <h4 className="font-semibold text-sm mb-2 text-gray-900">About PDF OCR</h4>
          <ul className="text-xs text-gray-600 space-y-1">
            <li>• Extracts text from scanned PDF documents using OCR</li>
            <li>• Preserves text formatting and layout information</li>
            <li>• Supports text, Word (DOCX), and JSON output formats</li>
            <li>• Word format provides formatted document with page headings</li>
            <li>• JSON format includes detailed positional data and bounding boxes</li>
            <li>• Download link expires after 7 days</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
